import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import type { BulkAction } from "@/components/molecules/tables/bulk-actions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useMessagesBulkActions } from "./bulk-actions";
import { SortMenu, type MessageSort } from "./filters";
import {
  FilterMenuPill,
  FilterPills,
  type FilterOption,
} from "@/components/molecules/filter-pills";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { ClientMessage } from "@/features/messages/types";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { MagnifyingGlassIcon, TrayIcon, XIcon } from "@phosphor-icons/react";
import {
  format,
  isThisWeek,
  isThisYear,
  isToday,
  isYesterday,
} from "date-fns";
import type { Locale } from "date-fns";
import { Fragment, useState } from "react";
import { MessageListItem } from "./message-list-item";

export type MessageFilter = "pending" | "handled";

/** What the page holds for the list: the checked rows and what changes them. */
export type MessageSelection = {
  checked: Map<string, ClientMessage>;
  check: (message: ClientMessage, on: boolean) => void;
  checkAll: (on: boolean) => void;
  clear: () => void;
};

/**
 * The left pane: the two views of the inbox, a search, who it is for, which
 * category, how to order and group them, and the requests themselves.
 *
 * The rows come from the page, which also needs them to move on to the next
 * request once one is closed.
 */
export function MessageList({
  filter,
  onFilterChange,
  counts,
  search,
  onSearchChange,
  assignee,
  onAssigneeChange,
  assigneeOptions,
  category,
  onCategoryChange,
  categoryOptions,
  sort,
  onSortChange,
  selection,
  messages,
  isLoading,
  isError,
  onRetry,
  filtering,
  selectedId,
  onSelect,
}: {
  filter: MessageFilter;
  onFilterChange: (filter: MessageFilter) => void;
  counts: Record<MessageFilter, number>;
  search: string;
  onSearchChange: (search: string) => void;
  assignee?: string;
  onAssigneeChange: (assignee?: string) => void;
  assigneeOptions: FilterOption<string>[];
  category?: string;
  onCategoryChange: (category?: string) => void;
  categoryOptions: FilterOption<string>[];
  sort: MessageSort;
  onSortChange: (sort: MessageSort) => void;
  selection: MessageSelection;
  messages: ClientMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** A search or a filter narrows the view: an empty list then says so. */
  filtering: boolean;
  selectedId?: string;
  onSelect: (message: ClientMessage) => void;
}) {
  const { t } = useTranslation("messages");

  // The rows are grouped as they are sorted: under their urgency, or under the
  // day / week / month they arrived (were closed, in the handled view).
  const locale = useDateFnsLocale();
  const groupOf = (message: ClientMessage) =>
    sort === "urgency"
      ? { key: message.urgency, label: t(`urgency.${message.urgency}`) }
      : dateGroup(
          filter === "handled" ? closedAt(message) : message.created_at || 0,
          t,
          locale,
        );
  const bulkActions = useMessagesBulkActions();
  const { checked } = selection;
  const allChecked =
    messages.length > 0 && messages.every((m) => checked.has(m.id));
  const someChecked = checked.size > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b p-3">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("list.searchPlaceholder")}
            className="pl-8 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label={t("list.clearSearch")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <FilterPills
            label={t("title")}
            value={filter}
            onChange={onFilterChange}
            className="gap-1.5"
            options={(["pending", "handled"] as const).map((key) => ({
              value: key,
              label: t(`filters.${key}`),
              count: counts[key],
            }))}
          />
          {assigneeOptions.length > 0 && (
            <FilterMenuPill
              label={t("filters.assignee")}
              clearLabel={t("filters.clear", { name: t("filters.assignee") })}
              value={assignee}
              onChange={onAssigneeChange}
              options={assigneeOptions}
              allLabel={t("filters.anyAssignee")}
            />
          )}
          <FilterMenuPill
            label={t("filters.category")}
            clearLabel={t("filters.clear", { name: t("filters.category") })}
            value={category}
            onChange={onCategoryChange}
            options={categoryOptions}
            allLabel={t("filters.anyCategory")}
          />
        </div>
      </div>

      {someChecked && (
        <SelectionBar
          rows={[...checked.values()]}
          actions={bulkActions}
          onClear={selection.clear}
        />
      )}

      <div className="flex items-center gap-2 border-b py-1.5 pr-2 pl-3">
        <Checkbox
          checked={allChecked ? true : someChecked ? "indeterminate" : false}
          onCheckedChange={(value) => selection.checkAll(value === true)}
          aria-label={t("list.selectAll")}
          disabled={messages.length === 0}
        />
        <span className="flex-1 text-xs text-muted-foreground">
          {t("list.count", { count: messages.length })}
        </span>
        <SortMenu value={sort} onChange={onSortChange} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4 p-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <ErrorState compact onRetry={onRetry} />
        ) : messages.length === 0 ? (
          <EmptyState
            compact
            icon={filtering ? MagnifyingGlassIcon : TrayIcon}
            title={
              filtering
                ? t("list.noResults")
                : filter === "pending"
                  ? t("empty.title")
                  : t("empty.filtered")
            }
            description={
              filtering
                ? undefined
                : filter === "pending"
                  ? t("empty.description")
                  : t("empty.handled")
            }
          />
        ) : (
          messages.map((message, index) => (
            <Fragment key={message.id}>
              {(index === 0 ||
                groupOf(message).key !== groupOf(messages[index - 1]).key) && (
                <GroupHeader label={groupOf(message).label} />
              )}
              <MessageListItem
                message={message}
                selected={message.id === selectedId}
                checked={checked.has(message.id)}
                onSelect={onSelect}
                onCheck={selection.check}
              />
            </Fragment>
          ))
        )}
      </div>
    </div>
  );
}

/** When a handled request was closed — the date its row prints and is sorted by. */
export const closedAt = (message: ClientMessage) =>
  message.handled_at || message.created_at || 0;

/** The bucket a date falls in: today, yesterday, this week, else its month. */
const dateGroup = (
  timestamp: number,
  t: (key: string) => string,
  locale: Locale,
) => {
  const date = new Date(timestamp);
  if (isToday(date)) return { key: "today", label: t("list.today") };
  if (isYesterday(date)) return { key: "yesterday", label: t("list.yesterday") };
  if (isThisWeek(date, { weekStartsOn: 1 }))
    return { key: "week", label: t("list.thisWeek") };
  const label = format(
    date,
    isThisYear(date) ? "LLLL" : "LLLL yyyy",
    { locale },
  );
  return { key: format(date, "yyyy-MM"), label };
};

/** The group a run of rows belongs to, as the call history separates its days. */
function GroupHeader({ label }: { label: string }) {
  return (
    <div className="sticky top-0 z-10 border-b bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  );
}

/** What to do with the checked rows, in one line of a narrow pane. */
function SelectionBar({
  rows,
  actions,
  onClear,
}: {
  rows: ClientMessage[];
  actions: BulkAction<ClientMessage>[];
  onClear: () => void;
}) {
  const { t } = useTranslation("messages");
  const [running, setRunning] = useState<string | null>(null);
  const visible = actions.filter((action) => action.isVisible?.(rows) ?? true);

  const runAction = async (action: BulkAction<ClientMessage>) => {
    setRunning(action.key);
    try {
      await action.onAction(rows);
      onClear();
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="flex items-center gap-1 border-b bg-primary/5 py-1.5 pr-2 pl-3">
      <span className="flex-1 text-sm font-medium">
        {t("list.selected", { count: rows.length })}
      </span>
      {visible.map((action) => (
        <Tooltip key={action.key}>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant={action.variant === "destructive" ? "ghost" : "outline"}
              className={cn(
                "size-8",
                action.variant === "destructive" && "text-destructive",
              )}
              aria-label={action.label}
              disabled={running !== null}
              confirm={action.confirm?.(rows)}
              onClick={() => runAction(action)}
            >
              {action.icon}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{action.label}</TooltipContent>
        </Tooltip>
      ))}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={t("list.clearSelection")}
            onClick={onClear}
            disabled={running !== null}
          >
            <XIcon className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("list.clearSelection")}</TooltipContent>
      </Tooltip>
    </div>
  );
}
